import { useEffect, useCallback, useRef, useMemo } from 'react';
import { updateSessionVariableValue } from '../utils/utils';
import { getCookie } from '../utils/cookieManager';
import { COOKIE_NAMES } from '../constants/constants';
import { verifyToken } from '../../store/api/authentication';
import store from '../../store/store';
import { SESSION_CONFIG, TimeUtils } from '../config/sessionConfig';

interface SessionManagerConfig {
  warningThreshold: number; // minutes before expiry to show warning
  activityExtension: number; // minutes to extend on activity
  heartbeatInterval: number; // minutes between heartbeat calls
}

const defaultConfig: SessionManagerConfig = {
  warningThreshold: SESSION_CONFIG.WARNING_THRESHOLD / (60 * 1000), // Convert to minutes
  activityExtension: SESSION_CONFIG.ACTIVITY_EXTENSION / (60 * 1000), // Convert to minutes
  heartbeatInterval: SESSION_CONFIG.HEARTBEAT_INTERVAL / (60 * 1000), // Convert to minutes
};

export const useSessionManager = (config: Partial<SessionManagerConfig> = {}) => {
  const mergedConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track user activity and extend session
  const trackUserActivity = useCallback(() => {
    const events = SESSION_CONFIG.ACTIVITY_EVENTS;

    const handleActivity = () => {
      // Clear existing timeout
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }

      // Set new timeout to extend session
      activityTimeoutRef.current = setTimeout(() => {
        extendSession(mergedConfig.activityExtension);
        if (SESSION_CONFIG.ENABLE_LOGGING) {
          console.log(`${SESSION_CONFIG.LOG_PREFIX} User activity detected - extending session by ${mergedConfig.activityExtension} minutes`);
        }
      }, 1000); // Debounce activity by 1 second
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []);

  // Extend session by specified minutes
  const extendSession = useCallback((minutes: number) => {
    const expirationTime = new Date(Date.now() + minutes * 60 * 1000);
    updateSessionVariableValue(COOKIE_NAMES.tokenExpiryTime, expirationTime.getTime(), expirationTime);
    if (SESSION_CONFIG.ENABLE_LOGGING) {
      console.log(`${SESSION_CONFIG.LOG_PREFIX} Session extended by ${minutes} minutes`);
    }
  }, []);

  // Check session status with backend
  const checkSessionStatus = useCallback(async () => {
    try {
      const response = await store.dispatch(verifyToken());
      
      if (response.payload && typeof response.payload === 'object' && 'status' in response.payload && response.payload.status === 201) {
        // Token is valid, check if it was refreshed
        const payload = response.payload as { status: number; data?: any };
        const refreshedToken = payload.data?.refreshedToken;
        
        if (refreshedToken && refreshedToken !== '') {
          if (SESSION_CONFIG.ENABLE_LOGGING) {
            console.log(`${SESSION_CONFIG.LOG_PREFIX} Token was refreshed by backend, updating session`);
          }
          extendSession(SESSION_CONFIG.JWT_TOTAL_DURATION / (60 * 1000)); // 2 hours for refreshed token
        } else {
          // Token is valid but not refreshed - don't extend session automatically
          if (SESSION_CONFIG.ENABLE_LOGGING) {
            console.log(`${SESSION_CONFIG.LOG_PREFIX} Token valid, no session extension needed`);
          }
          // Don't extend session here - only extend on actual user activity
        }
        return true;
      } else {
        if (SESSION_CONFIG.ENABLE_LOGGING) {
          console.log(`${SESSION_CONFIG.LOG_PREFIX} Token validation failed`);
        }
        return false;
      }
    } catch (error) {
      console.error('Session check failed:', error);
      return false;
    }
  }, [extendSession]);

  // Heartbeat to check token validity and detect backend token refresh
  // This does NOT extend session - only user activity extends session
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(async () => {
      if (SESSION_CONFIG.ENABLE_LOGGING) {
        console.log(`${SESSION_CONFIG.LOG_PREFIX} Heartbeat: Checking token validity with verifyToken API`);
      }
      const isValid = await checkSessionStatus();
      if (!isValid) {
        // Session is invalid, stop heartbeat
        if (SESSION_CONFIG.ENABLE_LOGGING) {
          console.log(`${SESSION_CONFIG.LOG_PREFIX} Heartbeat: Token invalid, stopping heartbeat`);
        }
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
      }
    }, mergedConfig.heartbeatInterval * 60 * 1000);
  }, [checkSessionStatus]);

  // Get remaining session time
  const getRemainingTime = useCallback(() => {
    const tokenExpiryTime = getCookie(COOKIE_NAMES.tokenExpiryTime);
    if (!tokenExpiryTime) return 0;
    
    const currentTime = Date.now();
    const timeRemaining = Number(tokenExpiryTime) - currentTime;
    return Math.max(0, timeRemaining);
  }, []);

  // Check if session is close to expiry
  const isSessionExpiringSoon = useCallback(() => {
    const remainingTime = getRemainingTime();
    const warningTime = mergedConfig.warningThreshold * 60 * 1000;
    return remainingTime > 0 && remainingTime <= warningTime;
  }, [getRemainingTime]);

  // Initialize session manager
  useEffect(() => {
    // Start activity tracking
    const cleanupActivity = trackUserActivity();
    
    // Start heartbeat
    startHeartbeat();

    // Cleanup on unmount
    return () => {
      cleanupActivity();
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [trackUserActivity, startHeartbeat]);

  return {
    extendSession,
    checkSessionStatus,
    getRemainingTime,
    isSessionExpiringSoon,
  };
};

export default useSessionManager;